import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Music, User, ArrowLeft, Mail, Lock, LogIn, Plus } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';

const Login = () => {
  const navigate = useNavigate();
  const { signInAsGuest, signInWithGoogle, signUpWithEmail, signInWithEmail, user, logout, authInProgress } = useAuth();
  const intendedAction = sessionStorage.getItem('intendedAction');
  const [mode, setMode] = useState(intendedAction === 'create' ? 'host' : 'guest');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignup, setIsSignup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const redirectAttempts = useRef(0);

  // Pre-fill guest name from sessionStorage
  useEffect(() => {
    const savedName = sessionStorage.getItem('guestName');
    if (savedName) {
      setDisplayName(savedName);
    }
  }, []);

  // Only redirect if a non-guest user exists and auth is not in progress.
  // Guest users arriving from "Create Session" need to upgrade to Google/Email,
  // so don't bounce them back to home.
  useEffect(() => {
    if (redirectAttempts.current > 3) {
      console.error('🚨 Too many redirect attempts, stopping to prevent loop');
      return;
    }

    if (user && !authInProgress && !isLoading) {
      // Don't redirect guests who need to upgrade auth for hosting
      if (user.isGuest && intendedAction === 'create') {
        return;
      }
      redirectAttempts.current += 1;
      navigate('/', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authInProgress, isLoading]);


  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true);
      await signInWithGoogle();
    } catch (err) {
      console.error('Google sign-in error', err);
      toast.error('Google sign-in failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Email and password required');
      return;
    }
    try {
      setIsLoading(true);
      if (isSignup) {
        await signUpWithEmail(email, password, displayName || email.split('@')[0]);
      } else {
        await signInWithEmail(email, password);
      }
    } catch (err) {
      console.error('Email auth error', err);
      toast.error(err.message || 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGuestLogin = async (e) => {
    e.preventDefault();
    
    if (!displayName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    if (displayName.trim().length < 2) {
      toast.error('Name must be at least 2 characters long');
      return;
    }

    setIsLoading(true);
    const timestamp = () => new Date().toISOString();
    console.log(`[${timestamp()}] 🔐 Starting sign in process for: ${displayName.trim()}`);
    try {
      const result = await signInAsGuest(displayName.trim());
      console.log(`[${timestamp()}] ✅ Sign in successful, result:`, result);
      // Clear any stored intended actions
      sessionStorage.removeItem('intendedAction');
      sessionStorage.removeItem('sessionId');
      
      // Navigation will be handled by the useEffect that watches for user changes
      console.log(`[${timestamp()}] 🔐 Sign in complete, user state should be set, waiting for redirect`);
    } catch (error) {
      console.error(`[${timestamp()}] Login error:`, error);
      toast.error('Failed to sign in');
    } finally {
      setIsLoading(false);
    }
  };

  // Emergency reset function
  const emergencyReset = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-gray-900 to-black flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Emergency Reset Button */}
        {redirectAttempts > 2 && (
          <div className="mb-4 p-3 bg-red-900 border border-red-600 rounded-lg">
            <p className="text-red-200 text-sm mb-2">Navigation loop detected!</p>
            <button
              onClick={emergencyReset}
              className="bg-red-600 text-white px-4 py-2 rounded text-sm w-full"
            >
              Emergency Reset
            </button>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Music className="w-10 h-10 text-purple-400 mr-3" />
            <h1 className="text-3xl font-bold text-white">JukeHive</h1>
          </div>
          <p className="text-gray-300">
            {user ? `Already signed in as ${user.displayName}` : 'Join the party as a guest'}
          </p>
        </div>

        {/* Login Form or Already Logged In Message */}
        <div className="card">
          {user && !(user.isGuest && intendedAction === 'create') ? (
            // User is already logged in (and not a guest trying to host)
            <div className="text-center space-y-4">
              <div className="text-green-400 mb-4">
                <User className="w-16 h-16 mx-auto" />
              </div>
              <h2 className="text-xl font-semibold text-white">Welcome Back!</h2>
              <p className="text-gray-400">
                You're already signed in as <span className="text-white font-medium">{user.displayName}</span>
              </p>
              <div className="space-y-3 mt-6">
                <button
                  onClick={() => navigate('/')}
                  className="btn-primary w-full"
                >
                  Continue to JukeHive
                </button>
                <button
                  onClick={async () => {
                    await logout();
                  }}
                  className="btn-secondary w-full"
                >
                  Sign Out and Use Different Name
                </button>
              </div>
            </div>
          ) : (
            <>
            {/* Mode toggle */}
            <div className="flex mb-6 bg-gray-800 rounded overflow-hidden">
              <button
                className={`flex-1 py-2 text-sm ${mode==='guest' ? 'bg-purple-600 text-white' : 'text-gray-400'}`}
                onClick={()=>setMode('guest')}
              >Guest</button>
              <button
                className={`flex-1 py-2 text-sm ${mode==='host' ? 'bg-purple-600 text-white' : 'text-gray-400'}`}
                onClick={()=>setMode('host')}
              >Host</button>
            </div>

            {mode === 'guest' ? (
              <form onSubmit={handleGuestLogin} className="space-y-6">
                <div>
                  <label htmlFor="displayName" className="block text-sm font-medium text-gray-300 mb-2">
                    Your Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      id="displayName"
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Enter your name"
                      className={`input-field pl-10 w-full ${
                        displayName.trim().length === 1 ? 'border-red-500 focus:border-red-500' : ''
                      }`}
                      maxLength={30}
                      minLength={2}
                      disabled={isLoading || authInProgress}
                      aria-describedby="login-name-hint"
                    />
                  </div>
                  {displayName.trim().length === 1 ? (
                    <p id="login-name-hint" className="text-xs text-red-400 mt-1 font-medium">
                      One more letter — names need at least 2 characters
                    </p>
                  ) : (
                    <p id="login-name-hint" className="text-xs text-gray-400 mt-1">
                      At least 2 characters — visible to the party
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading || authInProgress || !displayName.trim()}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isLoading || authInProgress ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Joining...
                    </>
                  ) : (
                    'Join as Guest'
                  )}
                </button>
              </form>
            ) : (
              <div className="space-y-4">
                <button
                  onClick={handleGoogleLogin}
                  disabled={isLoading || authInProgress}
                  className="btn-primary w-full flex items-center justify-center disabled:opacity-50"
                >
                  {isLoading && mode==='host' ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  ) : null}
                  Continue with Google
                </button>

                <form onSubmit={handleEmailAuth} className="space-y-4">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e)=>setEmail(e.target.value)}
                      placeholder="Email"
                      className="input-field pl-10 w-full"
                      disabled={isLoading || authInProgress}
                    />
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="password"
                      value={password}
                      onChange={(e)=>setPassword(e.target.value)}
                      placeholder="Password"
                      className="input-field pl-10 w-full"
                      disabled={isLoading || authInProgress}
                    />
                  </div>

                  {isSignup && (
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={displayName}
                        onChange={(e)=>setDisplayName(e.target.value)}
                        placeholder="Display name"
                        className="input-field pl-10 w-full"
                        disabled={isLoading || authInProgress}
                      />
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading || authInProgress}
                    className="btn-primary w-full flex items-center justify-center disabled:opacity-50"
                  >
                    {isLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    ) : null}
                    {isSignup ? (<><Plus className="w-4 h-4 mr-2"/>Create&nbsp;Account</>) : (<><LogIn className="w-4 h-4 mr-2"/>Sign&nbsp;In</>)}
                  </button>
                  <p className="text-center text-xs text-gray-400">
                    {isSignup ? 'Already have an account?' : "Don't have an account?"}{' '}
                    <button type="button" onClick={()=>setIsSignup(!isSignup)} className="underline">
                      {isSignup ? 'Sign in' : 'Sign up'}
                    </button>
                  </p>
                </form>
              </div>
            )}
            </>
          )}

          {/* Back to Home */}
          <div className="mt-6 pt-6 border-t border-gray-700">
            <button
              onClick={() => navigate('/')}
              className="w-full flex items-center justify-center text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </button>
          </div>
        </div>

        {/* Info */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-400">
            Guest accounts are temporary and will be deleted when you sign out
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Login; 